/**
 * VAT Calculation Utilities for Saudi Arabia (15% VAT rate)
 * Handles both VAT-inclusive and VAT-exclusive calculations
 */

// Saudi Arabia VAT rate (15%)
export const VAT_RATE = 0.15;
export const VAT_MULTIPLIER = 1 + VAT_RATE; // 1.15

/**
 * Calculate VAT breakdown from VAT-inclusive amount
 * @param {number} amount - The total amount including VAT
 * @returns {Object} - { baseAmount, vatAmount, total }
 */
export const calculateFromInclusive = (amount) => {
  const numericAmount = parseFloat(amount) || 0;
  
  if (numericAmount < 0) {
    throw new Error('Amount cannot be negative');
  }
  
  const baseAmount = numericAmount / VAT_MULTIPLIER;
  const vatAmount = numericAmount - baseAmount;
  
  return {
    baseAmount: Math.round(baseAmount * 100) / 100, // Round to 2 decimal places
    vatAmount: Math.round(vatAmount * 100) / 100,
    total: numericAmount
  };
};

/**
 * Calculate VAT breakdown from VAT-exclusive amount
 * @param {number} amount - The base amount excluding VAT
 * @returns {Object} - { baseAmount, vatAmount, total }
 */
export const calculateFromExclusive = (amount) => {
  const numericAmount = parseFloat(amount) || 0;
  
  if (numericAmount < 0) {
    throw new Error('Amount cannot be negative');
  }
  
  const baseAmount = numericAmount;
  const vatAmount = numericAmount * VAT_RATE;
  const total = baseAmount + vatAmount;
  
  return {
    baseAmount: Math.round(baseAmount * 100) / 100,
    vatAmount: Math.round(vatAmount * 100) / 100,
    total: Math.round(total * 100) / 100
  };
};

/**
 * Validate that VAT calculation is correct
 * @param {number} baseAmount - Base amount without VAT
 * @param {number} vatAmount - VAT amount
 * @param {number} total - Total amount
 * @param {Object} options - Validation options
 * @returns {Object} - Detailed validation result
 */
export const validateCalculation = (baseAmount, vatAmount, total, options = {}) => {
  const { tolerance = 0.01, strictMode = false } = options;
  
  try {
    // Validate input parameters
    const baseValidation = validateAmount(baseAmount, { fieldName: 'Base amount', allowZero: true });
    const vatValidation = validateAmount(vatAmount, { fieldName: 'VAT amount', allowZero: true });
    const totalValidation = validateAmount(total, { fieldName: 'Total amount', allowZero: true });
    
    if (!baseValidation.isValid) {
      return {
        isValid: false,
        error: baseValidation.error,
        details: { field: 'baseAmount', value: baseAmount }
      };
    }
    
    if (!vatValidation.isValid) {
      return {
        isValid: false,
        error: vatValidation.error,
        details: { field: 'vatAmount', value: vatAmount }
      };
    }
    
    if (!totalValidation.isValid) {
      return {
        isValid: false,
        error: totalValidation.error,
        details: { field: 'total', value: total }
      };
    }
    
    const calculatedTotal = baseValidation.numericAmount + vatValidation.numericAmount;
    const difference = Math.abs(calculatedTotal - totalValidation.numericAmount);
    
    // Check if calculation is within tolerance
    if (difference > tolerance) {
      return {
        isValid: false,
        error: `VAT calculation mismatch. Expected total: ${calculatedTotal.toFixed(2)}, got: ${totalValidation.numericAmount.toFixed(2)}`,
        details: {
          expectedTotal: calculatedTotal,
          actualTotal: totalValidation.numericAmount,
          difference: difference,
          tolerance: tolerance
        }
      };
    }
    
    // In strict mode, also validate VAT rate
    if (strictMode && baseValidation.numericAmount > 0) {
      const expectedVAT = baseValidation.numericAmount * VAT_RATE;
      const vatDifference = Math.abs(expectedVAT - vatValidation.numericAmount);
      
      if (vatDifference > tolerance) {
        return {
          isValid: false,
          error: `VAT rate validation failed. Expected VAT: ${expectedVAT.toFixed(2)}, got: ${vatValidation.numericAmount.toFixed(2)}`,
          details: {
            expectedVAT: expectedVAT,
            actualVAT: vatValidation.numericAmount,
            vatDifference: vatDifference,
            vatRate: VAT_RATE
          }
        };
      }
    }
    
    return {
      isValid: true,
      error: null,
      details: {
        baseAmount: baseValidation.numericAmount,
        vatAmount: vatValidation.numericAmount,
        total: totalValidation.numericAmount,
        difference: difference
      }
    };
    
  } catch (error) {
    return {
      isValid: false,
      error: `Validation error: ${error.message}`,
      details: { originalError: error.message }
    };
  }
};

/**
 * Calculate VAT breakdown based on inclusion preference
 * @param {number} amount - The entered amount
 * @param {boolean} includesVAT - Whether the amount includes VAT
 * @returns {Object} - { baseAmount, vatAmount, total, includesVAT }
 */
export const calculateVATBreakdown = (amount, includesVAT) => {
  const calculation = includesVAT 
    ? calculateFromInclusive(amount)
    : calculateFromExclusive(amount);
    
  return {
    ...calculation,
    includesVAT
  };
};

/**
 * Validate input amount for VAT calculations
 * @param {any} amount - The amount to validate
 * @param {Object} options - Validation options
 * @returns {Object} - { isValid, error, numericAmount, warnings }
 */
export const validateAmount = (amount, options = {}) => {
  const { 
    maxAmount = 1000000, 
    minAmount = 0, 
    allowZero = true,
    fieldName = 'Amount'
  } = options;
  
  const warnings = [];
  
  // Check if amount is provided
  if (amount === null || amount === undefined || amount === '') {
    return {
      isValid: false,
      error: `${fieldName} is required`,
      numericAmount: 0,
      warnings
    };
  }
  
  const numericAmount = parseFloat(amount);
  
  // Check if amount is a valid number
  if (isNaN(numericAmount)) {
    return {
      isValid: false,
      error: `${fieldName} must be a valid number`,
      numericAmount: 0,
      warnings
    };
  }
  
  // Check for negative amounts
  if (numericAmount < 0) {
    return {
      isValid: false,
      error: `${fieldName} cannot be negative`,
      numericAmount: 0,
      warnings
    };
  }
  
  // Check for zero amounts if not allowed
  if (!allowZero && numericAmount === 0) {
    return {
      isValid: false,
      error: `${fieldName} must be greater than zero`,
      numericAmount: 0,
      warnings
    };
  }
  
  // Check minimum amount
  if (numericAmount < minAmount) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${minAmount}`,
      numericAmount: 0,
      warnings
    };
  }
  
  // Check maximum amount
  if (numericAmount > maxAmount) {
    return {
      isValid: false,
      error: `${fieldName} cannot exceed ${maxAmount.toLocaleString()}`,
      numericAmount: 0,
      warnings
    };
  }
  
  // Add warnings for unusual amounts
  if (numericAmount > maxAmount * 0.8) {
    warnings.push(`${fieldName} is unusually high. Please verify the amount.`);
  }
  
  if (numericAmount > 0 && numericAmount < 1) {
    warnings.push(`${fieldName} is very small. Please verify the amount.`);
  }
  
  return {
    isValid: true,
    error: null,
    numericAmount,
    warnings
  };
};

/**
 * Format amount for display with proper decimal places
 * @param {number} amount - Amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted amount
 */
export const formatAmount = (amount, decimals = 2) => {
  return parseFloat(amount).toFixed(decimals);
};

/**
 * Calculate total VAT breakdown for multiple cost types
 * @param {Object} costs - Object with cost types and their amounts/preferences
 * @returns {Object} - Aggregated VAT breakdown
 */
export const calculateTotalVATBreakdown = (costs) => {
  let totalBase = 0;
  let totalVAT = 0;
  let totalAmount = 0;
  
  const breakdown = {};
  
  Object.entries(costs).forEach(([costType, { amount, includesVAT }]) => {
    if (amount && amount > 0) {
      const calculation = calculateVATBreakdown(amount, includesVAT);
      breakdown[costType] = calculation;
      
      totalBase += calculation.baseAmount;
      totalVAT += calculation.vatAmount;
      totalAmount += calculation.total;
    }
  });
  
  return {
    breakdown,
    totals: {
      baseAmount: Math.round(totalBase * 100) / 100,
      vatAmount: Math.round(totalVAT * 100) / 100,
      total: Math.round(totalAmount * 100) / 100
    }
  };
};

/**
 * Comprehensive VAT validation for costing data
 * @param {Object} costingData - The costing data to validate
 * @returns {Object} - Validation result with detailed errors
 */
export const validateCostingVAT = (costingData) => {
  const errors = [];
  const warnings = [];
  const validatedData = {};
  
  try {
    // Validate manual fuel costs (new separated fuel system)
    if (costingData.manualFuelAmount !== undefined && costingData.manualFuelAmount !== null) {
      const manualFuelAmountValidation = validateAmount(costingData.manualFuelAmount, {
        fieldName: 'Manual fuel amount',
        maxAmount: 100000,
        allowZero: true
      });
      
      if (!manualFuelAmountValidation.isValid) {
        errors.push(manualFuelAmountValidation.error);
      } else {
        warnings.push(...manualFuelAmountValidation.warnings);
        
        // Validate manual fuel VAT calculation if amounts are provided
        if (costingData.manualFuelBaseAmount !== undefined && costingData.manualFuelVATAmount !== undefined) {
          const manualFuelVATValidation = validateCalculation(
            costingData.manualFuelBaseAmount,
            costingData.manualFuelVATAmount,
            manualFuelAmountValidation.numericAmount,
            { strictMode: false, tolerance: 0.05 } // More lenient tolerance for manual fuel
          );
          
          if (!manualFuelVATValidation.isValid) {
            errors.push(`Manual fuel VAT calculation error: ${manualFuelVATValidation.error}`);
          }
        }
        
        validatedData.manualFuelAmount = manualFuelAmountValidation.numericAmount;
      }
    }
    
    // Validate PetroApp fuel costs (new separated fuel system)
    if (costingData.petroAppFuelAmount !== undefined && costingData.petroAppFuelAmount !== null) {
      const petroAppFuelAmountValidation = validateAmount(costingData.petroAppFuelAmount, {
        fieldName: 'PetroApp fuel amount',
        maxAmount: 100000,
        allowZero: true
      });
      
      if (!petroAppFuelAmountValidation.isValid) {
        errors.push(petroAppFuelAmountValidation.error);
      } else {
        warnings.push(...petroAppFuelAmountValidation.warnings);
        
        // Validate PetroApp fuel VAT calculation if amounts are provided
        if (costingData.petroAppFuelBaseAmount !== undefined && costingData.petroAppFuelVATAmount !== undefined) {
          const petroAppFuelVATValidation = validateCalculation(
            costingData.petroAppFuelBaseAmount,
            costingData.petroAppFuelVATAmount,
            petroAppFuelAmountValidation.numericAmount,
            { strictMode: false, tolerance: 0.05 } // More lenient tolerance for PetroApp fuel
          );
          
          if (!petroAppFuelVATValidation.isValid) {
            errors.push(`PetroApp fuel VAT calculation error: ${petroAppFuelVATValidation.error}`);
          }
        }
        
        validatedData.petroAppFuelAmount = petroAppFuelAmountValidation.numericAmount;
      }
    }
    
    // Validate legacy fuel costs (for backward compatibility)
    if (costingData.fuelAmount !== undefined && costingData.fuelAmount !== null) {
      const fuelAmountValidation = validateAmount(costingData.fuelAmount, {
        fieldName: 'Fuel amount',
        maxAmount: 100000,
        allowZero: true
      });
      
      if (!fuelAmountValidation.isValid) {
        errors.push(fuelAmountValidation.error);
      } else {
        warnings.push(...fuelAmountValidation.warnings);
        
        // Validate fuel VAT calculation if amounts are provided
        if (costingData.fuelBaseAmount !== undefined && costingData.fuelVATAmount !== undefined) {
          const fuelVATValidation = validateCalculation(
            costingData.fuelBaseAmount,
            costingData.fuelVATAmount,
            fuelAmountValidation.numericAmount,
            { strictMode: false, tolerance: 0.05 } // More lenient tolerance for legacy fuel
          );
          
          if (!fuelVATValidation.isValid) {
            errors.push(`Fuel VAT calculation error: ${fuelVATValidation.error}`);
          }
        }
        
        validatedData.fuelAmount = fuelAmountValidation.numericAmount;
      }
    }
    
    // Validate maintenance costs
    if (costingData.maintenanceAmount !== undefined && costingData.maintenanceAmount !== null) {
      const maintenanceAmountValidation = validateAmount(costingData.maintenanceAmount, {
        fieldName: 'Maintenance amount',
        maxAmount: 100000,
        allowZero: true
      });
      
      if (!maintenanceAmountValidation.isValid) {
        errors.push(maintenanceAmountValidation.error);
      } else {
        warnings.push(...maintenanceAmountValidation.warnings);
        
        // Validate maintenance VAT calculation if amounts are provided
        if (costingData.maintenanceBaseAmount !== undefined && costingData.maintenanceVATAmount !== undefined) {
          const maintenanceVATValidation = validateCalculation(
            costingData.maintenanceBaseAmount,
            costingData.maintenanceVATAmount,
            maintenanceAmountValidation.numericAmount,
            { strictMode: false, tolerance: 0.05 } // More lenient tolerance for maintenance
          );
          
          if (!maintenanceVATValidation.isValid) {
            errors.push(`Maintenance VAT calculation error: ${maintenanceVATValidation.error}`);
          }
        }
        
        validatedData.maintenanceAmount = maintenanceAmountValidation.numericAmount;
      }
    }
    
    // Validate commission costs (no VAT)
    if (costingData.commissionAmount !== undefined && costingData.commissionAmount !== null) {
      const commissionAmountValidation = validateAmount(costingData.commissionAmount, {
        fieldName: 'Commission amount',
        maxAmount: 100000,
        allowZero: true
      });
      
      if (!commissionAmountValidation.isValid) {
        errors.push(commissionAmountValidation.error);
      } else {
        warnings.push(...commissionAmountValidation.warnings);
        validatedData.commissionAmount = commissionAmountValidation.numericAmount;
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      validatedData
    };
    
  } catch (error) {
    return {
      isValid: false,
      errors: [`Validation error: ${error.message}`],
      warnings: [],
      validatedData: {}
    };
  }
};

/**
 * Error handler for VAT calculation failures
 * @param {Error} error - The error that occurred
 * @param {Object} context - Additional context about the error
 * @returns {Object} - Formatted error response
 */
export const handleVATCalculationError = (error, context = {}) => {
  const { operation = 'VAT calculation', amount, includesVAT } = context;
  
  console.error(`${operation} failed:`, error, context);
  
  // Determine user-friendly error message
  let userMessage = 'An error occurred during VAT calculation. Please try again.';
  let fallbackData = null;
  
  if (error.message.includes('negative')) {
    userMessage = 'Amount cannot be negative. Please enter a positive value.';
  } else if (error.message.includes('number')) {
    userMessage = 'Please enter a valid number for the amount.';
  } else if (error.message.includes('calculation')) {
    userMessage = 'VAT calculation failed. Please verify the entered amount.';
    
    // Provide fallback calculation if possible
    if (amount && !isNaN(parseFloat(amount))) {
      try {
        fallbackData = calculateVATBreakdown(parseFloat(amount), includesVAT || true);
      } catch (fallbackError) {
        console.error('Fallback calculation also failed:', fallbackError);
      }
    }
  }
  
  return {
    success: false,
    error: userMessage,
    technicalError: error.message,
    fallbackData,
    timestamp: new Date().toISOString()
  };
};

/**
 * Fallback behavior when preferences can't be loaded
 * @param {string} costType - The cost type (fuel or maintenance)
 * @param {Error} error - The error that occurred
 * @returns {boolean} - Default VAT inclusion preference
 */
export const getVATPreferenceFallback = (costType, error = null) => {
  if (error) {
    console.warn(`Failed to load VAT preference for ${costType}:`, error);
  }
  
  // Default to VAT-inclusive for both fuel and maintenance
  // This matches the most common scenario in Saudi Arabia
  return true;
};

/**
 * Validate and sanitize VAT preferences
 * @param {Object} preferences - Raw preferences object
 * @returns {Object} - Sanitized preferences with fallbacks
 */
export const sanitizeVATPreferences = (preferences) => {
  const sanitized = {
    fuelVATDefault: true,
    maintenanceVATDefault: true
  };
  
  if (preferences && typeof preferences === 'object') {
    // Validate fuel preference
    if (typeof preferences.fuelVATDefault === 'boolean') {
      sanitized.fuelVATDefault = preferences.fuelVATDefault;
    }
    
    // Validate maintenance preference
    if (typeof preferences.maintenanceVATDefault === 'boolean') {
      sanitized.maintenanceVATDefault = preferences.maintenanceVATDefault;
    }
  }
  
  return sanitized;
};

/**
 * Client-side validation for form submission
 * @param {Object} formData - Form data to validate
 * @returns {Object} - Validation result
 */
export const validateFormSubmission = (formData) => {
  const errors = [];
  const warnings = [];
  
  try {
    // Check if at least one cost is entered
    const hasFuel = formData.fuelAmount && parseFloat(formData.fuelAmount) > 0;
    const hasMaintenance = formData.maintenanceAmount && parseFloat(formData.maintenanceAmount) > 0;
    const hasCommission = formData.commissionAmount && parseFloat(formData.commissionAmount) > 0;
    
    if (!hasFuel && !hasMaintenance && !hasCommission) {
      errors.push('Please enter at least one cost amount before saving.');
    }
    
    // Validate individual costs
    const costingValidation = validateCostingVAT(formData);
    errors.push(...costingValidation.errors);
    warnings.push(...costingValidation.warnings);
    
    // Check for required fields
    if (!formData.shipmentId) {
      errors.push('Shipment ID is required.');
    }
    
    if (!formData.companyId) {
      errors.push('Company ID is required.');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      canSubmit: errors.length === 0
    };
    
  } catch (error) {
    return {
      isValid: false,
      errors: [`Form validation error: ${error.message}`],
      warnings: [],
      canSubmit: false
    };
  }
};

// Export all functions as default object for easier importing
export default {
  VAT_RATE,
  VAT_MULTIPLIER,
  calculateFromInclusive,
  calculateFromExclusive,
  validateCalculation,
  calculateVATBreakdown,
  validateAmount,
  formatAmount,
  calculateTotalVATBreakdown,
  validateCostingVAT,
  handleVATCalculationError,
  getVATPreferenceFallback,
  sanitizeVATPreferences,
  validateFormSubmission
};